


import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Radio,
  Divider,
  CircularProgress,
  IconButton,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';

import {
  ArrowBack,
  Lock,
  LocalOffer,
  CardGiftcard,
  Savings,
} from '@mui/icons-material';
import  { fetchUserFromRedis } from "../HomePage/AuthonticationScreens/Register";
import CustomDialog from "../../CustomDialog";

const MedicinePaymentScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [selected, setSelected] = useState('online');
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [user, setUser] = useState(null);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);

  const [prescriptionUrl, setPrescriptionUrl] = useState(null);
  const [uploadingPrescription, setUploadingPrescription] = useState(false);

  const getCartData = () => {
    // First try location.state
    if (location.state?.cart && location.state.cart.length > 0) {
      return location.state;
    }
    
    // Fall back to sessionStorage
    try {
      const savedCart = sessionStorage.getItem('pharmacyCart');
      const savedPatientDetails = sessionStorage.getItem('pharmacyPatientDetails');
      const savedDeliveryAddress = sessionStorage.getItem('pharmacyDeliveryAddress');
      const savedPrescription = sessionStorage.getItem('pharmacyPrescription');
      
      if (savedCart) {
        const cart = JSON.parse(savedCart);
        const patientDetails = savedPatientDetails ? JSON.parse(savedPatientDetails) : null;
        const deliveryAddress = savedDeliveryAddress ? JSON.parse(savedDeliveryAddress) : null;
        
        if (cart.length > 0) {
          console.log('✅ Cart data restored from sessionStorage');
          
          // Calculate total from cart
          const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
          const deliveryCharge = Number(process.env.REACT_APP_DELIVERY_CHARGE) || 0;
          const totalAmount = subtotal + deliveryCharge;
          
          return {
            cart,
            orderType: location.state?.orderType || 'prescription',
            totalAmount,
            patientDetails,
            prescriptionFile: savedPrescription,
            deliveryAddress,
            userEmail: location.state?.userEmail || deliveryAddress?.full_name,
            userPhone: location.state?.userPhone || deliveryAddress?.phone,
            appliedPromo: location.state?.appliedPromo || null,
            subscriptionDiscount: location.state?.subscriptionDiscount || 0,
            subscriptionBenefits: location.state?.subscriptionBenefits || null,
            promoDiscount: location.state?.promoDiscount || 0,
            promoCode: location.state?.promoCode || ""
          };
        }
      }
    } catch (error) {
      console.error('Error restoring cart from sessionStorage:', error);
    }
    
    return location.state || {};
  };
  
  const cartData = getCartData();

  const { 
    cart, 
    orderType, 
    totalAmount, 
    deliveryAddress, 
    userEmail, 
    userPhone,
    patientDetails,
    prescriptionFile,
    appliedPromo,
    subscriptionDiscount = 0,
    promoDiscount = 0,
    promoCode = "",
    subscriptionBenefits = null  
  } = cartData;

  const finalSubscriptionDiscount = Number(subscriptionDiscount);

  const finalPromoDiscount = Number(promoDiscount) || 0;
  const totalSavings = finalSubscriptionDiscount + finalPromoDiscount;
  const uploadPrescriptionToServer = async (prescriptionData) => {
    try {
      setUploadingPrescription(true);
      console.log(' Uploading prescription...', prescriptionData);
      
      // Convert base64 to blob
      const base64Response = await fetch(prescriptionData);
      const blob = await base64Response.blob();
      
      // Determine file extension based on MIME type
      let extension = 'jpg'; // default
      if (blob.type.includes('pdf')) {
        extension = 'pdf';
      } else if (blob.type.includes('png')) {
        extension = 'png';
      } else if (blob.type.includes('jpeg') || blob.type.includes('jpg')) {
        extension = 'jpg';
      }
      
      const formData = new FormData();
      formData.append('prescription', blob, `prescription.${extension}`); // ← Dynamic filename
      formData.append('user_email', userEmail || user?.email || 'guest');
      formData.append('timestamp', Date.now().toString());
  
      const response = await fetch(`${process.env.REACT_APP_PRESCRITON_BASE_API_URL}/prescriptions/upload-temp`, {
        method: 'POST',
        body: formData,
      });
  
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }
  
      const result = await response.json();
      console.log("prescription upload result", result);
      
      if (result.success && result.prescription_url) {
        setPrescriptionUrl(result.prescription_url);
        console.log(' Prescription uploaded:', result.prescription_url);
        return result.prescription_url;
      }
      
      return null;
    } catch (error) {
      console.error(' Prescription upload error:', error);
      return null;
    } finally {
      setUploadingPrescription(false);
    }
  };

  // Rename temp prescription to order ID after order creation
  const renameTempPrescription = async (tempUrl, orderId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_PRESCRITON_BASE_API_URL}/prescriptions/rename-temp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          temp_url: tempUrl,
          order_id: orderId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to rename prescription file');
      }

      const result = await response.json();
      console.log(' Prescription renamed:', result.new_url);
      return result.new_url;
    } catch (error) {
      console.error(' Error renaming prescription:', error);
      return tempUrl; 
    }
  };
  const clearPrescriptionData = () => {
    try {
      sessionStorage.removeItem('pharmacyPrescription');
      sessionStorage.removeItem('tempPrescriptionUrl');
      setPrescriptionUrl(null);
      console.log(' Prescription data cleared successfully');
    } catch (error) {
      console.error('Error clearing prescription data:', error);
    }
  };
  useEffect(() => {
    const uploadPrescription = async () => {
      if (prescriptionFile) {
        const url = await uploadPrescriptionToServer(prescriptionFile);
        if (url) {
          console.log('Temp prescription URL stored:', url);
        }
      }
    };
    
    uploadPrescription();
  }, [prescriptionFile, userEmail, user]);


  const paymentOptions = [
    {
      id: 'online',
      label: 'Pay Online',
      subtitle: 'Card, UPI, Net Banking & more',
      icon: '💳',
      badge: 'Instant Confirmation',
      recommended: true,
    },
    {
      id: 'offline',
      label: 'Cash on Delivery',
      subtitle: 'Pay when you receive',
      icon: '💵',
      badge: 'Pay Later',
      recommended: false,
    },
  ];

  // Load Razorpay script
  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        // Check if already loaded
        if (window.Razorpay) {
          setRazorpayLoaded(true);
          resolve(true);
          return;
        }

        const script = document.createElement('script');
        script.src = process.env.REACT_APP_RAZORPAY_SCRIPT;
        script.async = true;
        
        script.onload = () => {
          console.log('✅ Razorpay script loaded successfully');
          setRazorpayLoaded(true);
          resolve(true);
        };
        
        script.onerror = () => {
          console.error('❌ Failed to load Razorpay script');
          setDialogMessage('Failed to load payment gateway. Please refresh and try again.');
          setDialogOpen(true);
          resolve(false);
        };
        
        document.body.appendChild(script);
      });
    };

    loadRazorpayScript();
  }, []);

  useEffect(() => {
    const loadUserData = async () => {
      const userId = sessionStorage.getItem("userId");
      const token = sessionStorage.getItem("token");
      if (!userId || !token) {
        setUser(null);
        return;
      }
      try {
        const userData = await fetchUserFromRedis(userId, token);
        setUser(userData || null);
      } catch (err) {
        console.error("Error fetching user from Redis:", err);
        setUser(null);
      }
    };
  
    loadUserData();
  }, []);

  useEffect(() => {
    if (!cart || !Array.isArray(cart) || cart.length === 0) {
      setDialogMessage('Cart is empty');
      setDialogOpen(true);
      navigate(-1);
      return;
    }

    if (!orderType) {
      setDialogMessage('Order type is missing');
      setDialogOpen(true);
      navigate(-1);
      return;
    }

    if (!deliveryAddress) {
      setDialogMessage('Delivery address is missing');
      setDialogOpen(true);
      navigate(-1);
      return;
    }

    console.log('✅ Route params validated:', {
      cart: cart.length,
      orderType,
      totalAmount,
      deliveryAddress: deliveryAddress.full_name,
    });
  }, [cart, orderType, deliveryAddress, totalAmount, navigate]);



  const createMedicineOrder = async (paymentData) => {
    try {
      setLoading(true);

      const userData = currentUser || {
        user_id: null,
        email: userEmail,
        phone: userPhone || deliveryAddress.phone,
      };

      console.log('userrrrrrline 565:::', userData);

      if (!userData.email) {
        throw new Error('User email is required');
      }

      const items = cart.map((item) => ({
        medicine_id: item.medicine_id,
        quantity: item.quantity,
      }));

      if (!items.every(item => item.medicine_id && item.quantity > 0)) {
        throw new Error('Invalid cart items');
      }
      const paymentDetails = {
        payment_id: paymentData.payment_id,
        order_id: paymentData.order_id,
        method: paymentData.method,
        status: paymentData.status,
        amount: paymentData.totalAmount,
        gateway: paymentData.gateway,
        currency: paymentData.currency
      };
      
      // const finalSubscriptionDiscount = Number(subscriptionDiscount || 0);
      const finalPromoDiscount = Number(promoDiscount || 0);
      const finalDiscount = finalSubscriptionDiscount + finalPromoDiscount;
      const orderPayload = {
        user_id: userData.user_id,
        user_email: userData.email,
        user_phone: deliveryAddress.phone,
        order_type: orderType,
        items: items,
        patient_details: patientDetails || {},
        delivery_address: {
          full_name: deliveryAddress.full_name,
          phone: deliveryAddress.phone,
          community_name: deliveryAddress.community_name,
          address_line1: deliveryAddress.address_line1,
          address_line2: deliveryAddress.address_line2 || '',
          landmark: deliveryAddress.landmark || '',
          city: deliveryAddress.city,
          state: deliveryAddress.state,
          pincode: deliveryAddress.pincode,
          address_type: deliveryAddress.address_type || 'home',
          delivery_time_option: deliveryAddress.delivery_time_option || '',
          delivery_time: deliveryAddress.delivery_time || '',
        },
        payment: {
          payment_id: paymentData.payment_id,
          order_id: paymentData.order_id || null,
          method: paymentData.method,
          status: paymentData.status,
          amount:paymentData.totalAmount,
          gateway: paymentData.gateway || null,
          currency: paymentData.currency || 'INR',
          transaction_history: paymentData.transaction_history || [],
        },
        totalAmount: totalAmount,
        paymentDetails: paymentDetails,
        notes: `Order from web app - ${orderType}`,
        subscription_discount: finalSubscriptionDiscount,  
        promo_discount: finalPromoDiscount,                
        promo_code: promoCode || "",                        
        discount: finalDiscount,  
      };
          // ADD PRESCRIPTION URL IF AVAILABLE
      if (prescriptionUrl) {
        orderPayload.prescription_url = prescriptionUrl;
        orderPayload.prescription_verified = false; // Will be verified by admin/pharmacist
        console.log('Adding prescription URL to order:', prescriptionUrl);
      } else if (prescriptionFile) {
        // If file exists but upload failed/pending, add a note
        orderPayload.notes += ' - Prescription upload pending';
        console.log(' Prescription file exists but URL not available yet');
      }

      console.log('📝 Creating medicine order with payload:', JSON.stringify(orderPayload, null, 2));

      const response = await fetch(process.env.REACT_APP_CREATE_MEDICINE_ORDER, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderPayload),
      });

      const responseText = await response.text();
      console.log(' Raw server response:', responseText);

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse response:', responseText);
        throw new Error('Invalid server response');
      }

      if (!response.ok) {
        console.error(' Order creation failed:', result);
        throw new Error(result.error || `Server error: ${response.status}`);
      }

      console.log(' Order created successfully:', result);
      if (prescriptionUrl && prescriptionUrl.includes('temp_')) {
        const permanentUrl = await renameTempPrescription(prescriptionUrl, result.order_id);
        console.log(' Prescription permanently stored:', permanentUrl);
      }
      sessionStorage.removeItem('pharmacyCart');
      
      // Clear patient details
      sessionStorage.removeItem('pharmacyPatientDetails');
      clearPrescriptionData();
      navigate('/medicine-order-success', {
        replace: true,
        state: {
          orderId: result.order_id,
          // totalAmount: result.total_amount,
          totalAmount: totalAmount,
          paymentMethod: paymentData.method,
          paymentStatus: result.payment_status,
          deliveryAddress: deliveryAddress,
          hasPrescription: !!prescriptionUrl,
        }
      });

      return true;
    } catch (error) {
      console.error('💥 Order creation error:', error);
      setDialogMessage(error.message || 'Failed to create order. Please contact support.');
      setDialogOpen(true);
      
      if (paymentData.status === 'success') {
        setDialogMessage('Your payment was successful but order creation failed. Please contact support with payment ID: ' + paymentData.payment_id);
        setDialogOpen(true);
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleOfflinePayment = async () => {
    console.log(' Processing cash on delivery...');

    const paymentData = {
      payment_id: `offline_${Date.now()}`,
      order_id: null,
      method: 'offline',
      status: 'pending',
      amount: totalAmount,
      gateway: null,
      currency: 'INR',
      transaction_history: [
        {
          timestamp: new Date().toISOString(),
          method: 'offline',
          status: 'pending',
        },
      ],
    };

    await createMedicineOrder(paymentData);
  };

  const handleOnlinePayment = async () => {
    // Check if Razorpay is loaded
    if (!razorpayLoaded || !window.Razorpay) {
      setDialogMessage('Payment gateway is loading. Please wait a moment and try again.');
      setDialogOpen(true);
      return;
    }

    if (!currentUser && !userEmail) {
      setDialogMessage('User information is missing');
      setDialogOpen(true);
      return;
    }

    const userData = currentUser || { email: userEmail };

    try {
      setLoading(true);
      console.log('💳 Starting online payment process...');

      const orderRes = await fetch(process.env.REACT_APP_CREATE_RAZORPAY_ORDER, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: totalAmount,
          currency: 'INR',
          receipt: `medicine_${Date.now()}`,
          notes: {
            order_type: orderType,
            customer_email: userData.email,
          },
        }),
      });

      const orderData = await orderRes.json();

      if (!orderRes.ok) {
        console.error(' Razorpay order creation failed:', orderData);
        throw new Error(orderData.error || 'Failed to create payment order');
      }

      console.log(' Razorpay order created:', orderData);
      setLoading(false);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY || "rzp_test_RLPNXraO5Rywls",
        amount: orderData.totalAmount,
        currency: orderData.currency,
        name: 'Ayuja Medicines',
        description: `Medicine Order - ${orderType}`,
        order_id: orderData.id,
        prefill: {
          name: deliveryAddress.full_name,
          email: userData.email,
          contact: deliveryAddress.phone,
        },
        theme: { color: '#006D77' },
        handler: async function (paymentSuccess) {
          console.log(' Payment Success:', paymentSuccess);
          setLoading(true);

          try {
            const verifyRes = await fetch(process.env.REACT_APP_PROCESS_PAYEMENT, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_payment_id: paymentSuccess.razorpay_payment_id,
                razorpay_order_id: paymentSuccess.razorpay_order_id,
                razorpay_signature: paymentSuccess.razorpay_signature,
                method: 'online',
                amount: orderData.totalAmount,
              }),
            });

            const paymentResult = await verifyRes.json();
            console.log('🔍 Payment verification:', paymentResult);

            if (paymentResult.status !== 'success') {
              throw new Error('Payment verification failed');
            }

            const onlinePaymentData = {
              payment_id: paymentSuccess.razorpay_payment_id,
              order_id: paymentSuccess.razorpay_order_id,
              method: 'online',
              status: 'success',
              amount: orderData.totalAmount / 100,
              gateway: 'razorpay',
              currency: 'INR',
              transaction_history: [
                {
                  timestamp: new Date().toISOString(),
                  method: 'online',
                  gateway: 'razorpay',
                  payment_id: paymentSuccess.razorpay_payment_id,
                  order_id: paymentSuccess.razorpay_order_id,
                  status: 'success',
                },
              ],
            };

            await createMedicineOrder(onlinePaymentData);
          } catch (error) {
            console.error(' Post-payment error:', error);
            setLoading(false);
            setDialogMessage('Payment successful but order creation failed. Please contact support with payment ID: ' + paymentSuccess.razorpay_payment_id);
            setDialogOpen(true);
          }
        },
        modal: {
          ondismiss: function() {
            console.log(' Payment cancelled');
            setLoading(false);
            
            if (window.confirm('Payment Cancelled. Try again or choose Cash on Delivery?')) {
              handleOnlinePayment();
            } else {
              setSelected('offline');
            }
          }
        }
      };

      const rzp = new window.Razorpay(options);
      
      rzp.on('payment.failed', function (response) {
        console.log(' Payment failed:', response.error);
        setLoading(false);
        const errorMessage = response.error.description || response.error.message || 'Payment cancelled';
        if (window.confirm(`Payment Failed: ${errorMessage}. Try again?`)) {
          handleOnlinePayment();
        }
      });

      rzp.open();
    } catch (error) {
      console.error(' Payment initialization error:', error);
      setDialogMessage(error.message || 'Failed to initialize payment. Please try again.');
      setDialogOpen(true);
      setLoading(false);
    }
  };

  const handleProceed = async () => {
    if (selected === 'offline') {
      await handleOfflinePayment();
    } else {
      await handleOnlinePayment();
    }
  };

  if (!cart || cart.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={40} sx={{ color: '#006D77' }} />
      </Box>
    );
  }
  const subtotal = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  
  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh", pb: 10 }}>
      {/* Header */}
      <Paper
        elevation={2}
        sx={{ position: "sticky", top: 0, bgcolor: "white", zIndex: 10 }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 2,
              px: 4,
            }}
          >
            <IconButton onClick={() => navigate(-1)} disabled={loading}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#0F172A" }}>
              Payment Method
            </Typography>
            <Box sx={{ width: 40 }} />
          </Box>
        </Container>
      </Paper>

      {/* Main Layout */}
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Grid container spacing={4} alignItems="flex-start">
          {/* LEFT COLUMN */}
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              pl: 4,
            }}
          >
            {/* Pay Online */}
            <Card
              onClick={() => !loading && setSelected("online")}
              elevation={3}
              sx={{
                width: "180%",
                borderRadius: "20px",
                cursor: "pointer",
                border:
                  selected === "online"
                    ? "2px solid #006D77"
                    : "1px solid #E5E7EB",
                bgcolor: "#E7F8D9",
                position: "relative",
                transition: "all 0.3s",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                p: 2,
              }}
            >
              {selected === "online" && (
                <Chip
                  label="Recommended"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: -10,
                    right: 20,
                    bgcolor: "#006D77",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "11px",
                    height: 24,
                    borderRadius: "10px",
                  }}
                />
              )}
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  px: 3,
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Pay Online{" "}
                    <Chip
                      label="Instant Confirmation"
                      size="small"
                      sx={{
                        bgcolor: "#D1FAE5",
                        color: "#006D77",
                        fontWeight: 600,
                        fontSize: "11px",
                        height: 22,
                        ml: 1,
                      }}
                    />
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "#0F172A", fontSize: "16px" }}
                  >
                    💳 Card, UPI, Net Banking & more
                  </Typography>
                </Box>
                <Radio
                  checked={selected === "online"}
                  sx={{ "&.Mui-checked": { color: "#006D77" } }}
                />
              </CardContent>
            </Card>

            {/* Cash on Delivery */}
            <Card
              onClick={() => !loading && setSelected("offline")}
              elevation={3}
              sx={{
                width: "180%",
                borderRadius: "20px",
                cursor: "pointer",
                border:
                  selected === "offline"
                    ? "2px solid #006D77"
                    : "1px solid #E5E7EB",
                bgcolor: "#E7F8D9",
                transition: "all 0.3s",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                p: 2,
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  px: 3,
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Cash on Delivery{" "}
                    <Chip
                      label="Pay later"
                      size="small"
                      sx={{
                        bgcolor: "#D1FAE5",
                        color: "#006D77",
                        fontWeight: 600,
                        fontSize: "11px",
                        height: 22,
                        ml: 1,
                      }}
                    />
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "#0F172A", fontSize: "16px" }}
                  >
                    🏠 Pay when you receive
                  </Typography>
                </Box>
                <Radio
                  checked={selected === "offline"}
                  sx={{ "&.Mui-checked": { color: "#006D77" } }}
                />
              </CardContent>
            </Card>

            {/* Security Info */}
            <Paper
              elevation={0}
              sx={{
                bgcolor: "#F0FDF4",
                borderRadius: 2,
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 3,
                width: "90%",
              }}
            >
              <Lock sx={{ fontSize: 16, color: "#059669" }} />
              <Typography variant="body2" sx={{ color: "#059669" }}>
                Your payment information is secure and encrypted
              </Typography>
            </Paper>

            {/* Delivery Address */}
            <Box sx={{ width: "90%" }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#0F172A", mb: 1 }}
              >
                Delivery Address
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
               Name: {deliveryAddress.full_name || "N/A"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#006D77", fontWeight: 600, mb: 0.5 }}
              >
                Phone:{deliveryAddress.phone || userPhone || "N/A"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#006D77", fontWeight: 600, mb: 0.5 }}
              >
               Community Name: {deliveryAddress.community_name || "N/A"}
              </Typography>
              
              <Typography variant="body2" sx={{ color: "#374151" }}>
               Block NO: {deliveryAddress.address_line1 || ""}
              </Typography>
              {deliveryAddress.address_line2 && (
                <Typography variant="body2" sx={{ color: "#374151" }}>
                  {deliveryAddress.address_line2}
                </Typography>
              )}
              {/* <Typography variant="body2" sx={{ color: "#374151" }}>
                {deliveryAddress.city || ""}, {deliveryAddress.state || ""} -{" "}
                {deliveryAddress.pincode || ""}
              </Typography> */}
            </Box>
          </Grid>
          {/* RIGHT COLUMN — ORDER SUMMARY */}
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              ml:50,
            }}
          >
            <Card
              elevation={2}
              sx={{
                borderRadius: "16px",
                border: "1.5px solid #bcd3de",
                boxShadow: "0px 3px 8px rgba(0, 0, 0, 0.08)",
                width: "320px",
                // height: "340px",
                backgroundColor: "#ffffff",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-evenly",
                p: 2,
              }}
            >
              <Grid item xs={12} md={6}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: "16px",
                    border: "1.5px solid #bcd3de",
                    boxShadow: "0px 3px 8px rgba(0, 0, 0, 0.08)",
                    p: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#0F172A", mb: 2 }}>
                    Patient Details
                  </Typography>

                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Name: {patientDetails?.name || "N/A"}
                  </Typography>

                  <Typography variant="body2" sx={{ color: "#006D77", fontWeight: 600 }}>
                    Age: {patientDetails?.dob || "N/A"}
                  </Typography>

                  <Typography variant="body2" sx={{ color: "#006D77", fontWeight: 600 }}>
                    Gender: {patientDetails?.gender || "N/A"}
                  </Typography>

                  <Typography variant="body2" sx={{ color: "#006D77", fontWeight: 600 }}>
                    Phone: {patientDetails?.phone || "N/A"}
                  </Typography>
                </Card>
              </Grid>
              <CardContent sx={{ p: 2 }}>


                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, mb: 2, color: "#0f172a" }}
                >
                  Order Summary
                </Typography>
            









<Box sx={{
  p: 2.5,
  borderRadius: 3,
  background: "linear-gradient(145deg, #f8fafc, #ffffff)",
  boxShadow: "0 8px 20px rgba(0,0,0,0.04)",
  border: "1px solid #e5e7eb"
}}>
  {/* Cart Items */}
  {cart.map((item) => (
    <Box key={item.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.name}
        </Typography>
        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, mt: 0.3, display: "block" }}>
          Quantity: {item.quantity}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 700, color: "#334155" }}>
        ₹{(item.price * item.quantity).toFixed(2)}
      </Typography>
    </Box>
  ))}

  <Divider sx={{ my: 2 }} />

  {/* Subtotal */}
  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
    <Typography variant="body2" sx={{ color: "#475569" }}>
      Subtotal
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>
      ₹{subtotal?.toFixed(2)}
    </Typography>
  </Box>

{/* Subscription Discount */}
{finalSubscriptionDiscount > 0 && (
  <Box sx={{ 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center", 
    mb: 1, 
    bgcolor: "#f0fdf4", 
    p: 1, 
    borderRadius: 1 
  }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <CardGiftcard sx={{ fontSize: 16, color: "#16a34a" }} />
      <Box>
        <Typography variant="body2" sx={{ color: "#16a34a", fontWeight: 600 }}>
          Subscription Discount
          {subscriptionBenefits?.benefits?.medicine_discount && 
            ` (${subscriptionBenefits.benefits.medicine_discount}%)`
          }
        </Typography>
        {subscriptionBenefits?.subscription?.name && (
          <Typography variant="caption" sx={{ color: "#059669", fontSize: "10px" }}>
            {subscriptionBenefits.subscription.name}
          </Typography>
        )}
      </Box>
    </Box>
    <Typography variant="body2" sx={{ color: "#16a34a", fontWeight: 700 }}>
      - ₹{finalSubscriptionDiscount.toFixed(2)}
    </Typography>
  </Box>
)}

  {/* Promo Code Discount */}
  {finalPromoDiscount > 0 && (
    <Box sx={{ 
      display: "flex", 
      justifyContent: "space-between", 
      alignItems: "center", 
      mb: 1, 
      bgcolor: "#fef3c7", 
      p: 1, 
      borderRadius: 1 
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <LocalOffer sx={{ fontSize: 16, color: "#d97706" }} />
        <Typography variant="body2" sx={{ color: "#d97706", fontWeight: 600 }}>
          Promo {promoCode && `(${promoCode})`}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: "#d97706", fontWeight: 700 }}>
        - ₹{finalPromoDiscount.toFixed(2)}
      </Typography>
    </Box>
  )}

  {/* Delivery Charge */}
  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
    <Typography variant="body2" sx={{ color: "#475569" }}>
      Delivery Charge
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color: "#16a34a" }}>
      <s style={{ color: "#cbd5e1", marginRight: 6 }}>₹50</s> FREE
    </Typography>
  </Box>

  <Divider sx={{ my: 2 }} />

  {/* Total Savings Highlight */}
  {totalSavings > 0 && (
    <Box sx={{ 
      display: "flex", 
      justifyContent: "space-between", 
      alignItems: "center", 
      mb: 2, 
      bgcolor: "#dcfce7", 
      p: 1.5, 
      borderRadius: 2, 
      border: "1px dashed #16a34a" 
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Savings sx={{ fontSize: 20, color: "#16a34a" }} />
        <Typography variant="body1" sx={{ color: "#16a34a", fontWeight: 700 }}>
          Total Savings
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ color: "#16a34a", fontWeight: 800 }}>
        ₹{totalSavings.toFixed(2)}
      </Typography>
    </Box>
  )}

  {/* Total Payable */}
  <Box sx={{ 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center", 
    bgcolor: "#f1f5f9", 
    p: 1.5, 
    borderRadius: 2 
  }}>
    <Typography variant="body1" sx={{ fontWeight: 800, color: "#020617" }}>
      Total Payable
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 900, color: "#1e40af" }}>
      ₹{totalAmount?.toFixed(2)}
    </Typography>
  </Box>
</Box>











              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Bottom Button */}
      <Paper
        elevation={10}
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: "white",
          p: 4,
          px: 3,
             
        }}
      >
        <Container maxWidth="s">
          <Button
            fullWidth
            variant="contained"
            // size="small"
            
            onClick={handleProceed}
            disabled={loading}
            sx={{
              bgcolor: "#1E5A8A",
              py: 2,
               px: 6, 
               minWidth: "100px",
              borderRadius: "12px",
              fontWeight: 700,
              fontSize: "16px",
              textTransform: "none",
              "&:hover": { bgcolor: "#164e73" },
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              <>Proceed to payment ₹{totalAmount.toFixed(2)}</>
            )}
          </Button>
        </Container>
      </Paper>

      <CustomDialog
        open={dialogOpen}
        message={dialogMessage}
        onClose={() => setDialogOpen(false)}
      />
    </Box>
  );
};

export default MedicinePaymentScreen;